import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { 
  Timer, 
  Play, 
  Square, 
  Volume2, 
  Crown, 
  Lock,
  Minus,
  Plus
} from 'lucide-react';

// Web Audio Scheduler Constants
const LOOKAHEAD = 25.0; // ms - how frequently to call scheduler
const SCHEDULE_AHEAD_TIME = 0.1; // seconds - how far ahead to schedule

const MetronomePage = () => {
  const { t } = useTranslation();
  const { isPremium } = useAuth();
  
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [volume, setVolume] = useState(0.7);
  const [beatsPerBar] = useState(4);
  const [subdivision, setSubdivision] = useState('quarter');
  const [currentBeat, setCurrentBeat] = useState(0);
  
  // Refs for Web Audio scheduler (avoids stale closures)
  const audioContextRef = useRef(null);
  const timerIdRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const currentBeatRef = useRef(0);
  const isPlayingRef = useRef(false);
  const bpmRef = useRef(120);
  const volumeRef = useRef(0.7);
  const subdivisionRef = useRef('quarter');
  const beatsPerBarRef = useRef(4);

  // Keep refs in sync with state
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { subdivisionRef.current = subdivision; }, [subdivision]);

  // Scheduler function - reads from refs, stable reference
  const schedulerRef = useRef(null);
  schedulerRef.current = () => {
    const ctx = audioContextRef.current;
    if (!ctx || !isPlayingRef.current) return;

    const currentBpm = bpmRef.current;
    const currentVol = volumeRef.current;
    const currentSub = subdivisionRef.current;
    const bpb = beatsPerBarRef.current;

    const quarterNote = 60.0 / currentBpm;
    const noteDuration = currentSub === 'eighth' ? quarterNote / 2 :
                         currentSub === 'sixteenth' ? quarterNote / 4 : quarterNote;
    const notesPerBar = currentSub === 'quarter' ? bpb :
                        currentSub === 'eighth' ? bpb * 2 : bpb * 4;

    while (nextNoteTimeRef.current < ctx.currentTime + SCHEDULE_AHEAD_TIME) {
      const isAccent = (currentBeatRef.current % notesPerBar) === 0;

      // Generate click
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = isAccent ? 1000 : 800;
      osc.type = 'sine';
      const vol = currentVol * (isAccent ? 1.0 : 0.7);
      gain.gain.setValueAtTime(vol, nextNoteTimeRef.current);
      gain.gain.exponentialRampToValueAtTime(0.001, nextNoteTimeRef.current + 0.05);
      osc.start(nextNoteTimeRef.current);
      osc.stop(nextNoteTimeRef.current + 0.05);

      // Update visual beat
      const displayBeat = Math.floor(currentBeatRef.current / (notesPerBar / bpb)) % bpb;
      setCurrentBeat(displayBeat);

      currentBeatRef.current = (currentBeatRef.current + 1) % notesPerBar;
      nextNoteTimeRef.current += noteDuration;
    }
  };

  // Start metronome
  const start = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }

    isPlayingRef.current = true;
    setIsPlaying(true);
    currentBeatRef.current = 0;
    nextNoteTimeRef.current = audioContextRef.current.currentTime;

    timerIdRef.current = setInterval(() => schedulerRef.current(), LOOKAHEAD);
  }, []);

  // Stop metronome
  const stop = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setCurrentBeat(0);
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, []);

  // Toggle play/stop
  const toggle = useCallback(() => {
    if (isPlayingRef.current) {
      stop();
    } else {
      start();
    }
  }, [start, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIdRef.current) clearInterval(timerIdRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  // BPM adjustment
  const adjustBpm = (delta) => {
    setBpm(prev => Math.max(40, Math.min(240, prev + delta)));
  };

  // Tap tempo
  const tapTimesRef = useRef([]);
  const handleTapTempo = () => {
    const now = Date.now();
    tapTimesRef.current.push(now);
    
    // Keep only last 4 taps
    if (tapTimesRef.current.length > 4) {
      tapTimesRef.current.shift();
    }
    
    if (tapTimesRef.current.length >= 2) {
      const intervals = [];
      for (let i = 1; i < tapTimesRef.current.length; i++) {
        intervals.push(tapTimesRef.current[i] - tapTimesRef.current[i - 1]);
      }
      const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const newBpm = Math.round(60000 / avgInterval);
      if (newBpm >= 40 && newBpm <= 240) {
        setBpm(newBpm);
      }
    }
    
    // Reset if gap too long
    setTimeout(() => {
      if (Date.now() - tapTimesRef.current[tapTimesRef.current.length - 1] > 2000) {
        tapTimesRef.current = [];
      }
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="metronome-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
            <Timer className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2">
            {t('metronome.title')}
          </h1>
          <p className="text-zinc-400">{t('metronome.subtitle')}</p>
        </motion.div>

        {/* Main Control */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#121212] border border-white/5 rounded-3xl p-8 mb-6"
        >
          {/* BPM Display */}
          <div className="text-center mb-8">
            <div className="font-mono text-8xl font-black text-white mb-2">
              {bpm}
            </div>
            <div className="text-zinc-400 text-lg">BPM</div>
          </div>

          {/* Beat Indicators */}
          <div className="flex justify-center gap-4 mb-8">
            {[...Array(beatsPerBar)].map((_, i) => (
              <div
                key={i}
                className={`w-6 h-6 rounded-full transition-all duration-100 ${
                  isPlaying && currentBeat === i
                    ? i === 0 
                      ? 'bg-amber scale-125 shadow-lg shadow-amber/50'
                      : 'bg-indigo-400 scale-110'
                    : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>

          {/* BPM Slider */}
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => adjustBpm(-5)}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                <Minus className="w-5 h-5" />
              </Button>
              <Slider
                value={[bpm]}
                onValueChange={(val) => setBpm(val[0])}
                min={40}
                max={240}
                step={1}
                className="flex-1"
              />
              <Button
                onClick={() => adjustBpm(5)}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex justify-between text-xs text-zinc-500 mt-1 px-10">
              <span>40</span>
              <span>240</span>
            </div>
          </div>

          {/* Play/Stop Button */}
          <div className="flex justify-center gap-4">
            <Button
              onClick={toggle}
              size="lg"
              className={`px-16 py-8 rounded-full font-bold text-xl ${
                isPlaying
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white'
              }`}
              data-testid="metronome-toggle"
            >
              {isPlaying ? (
                <>
                  <Square className="w-6 h-6 mr-2 fill-current" />
                  {t('metronome.stop')}
                </>
              ) : (
                <>
                  <Play className="w-6 h-6 mr-2 fill-current" />
                  {t('metronome.start')}
                </>
              )}
            </Button>
          </div>

          {/* Tap Tempo */}
          <div className="mt-6 text-center">
            <Button
              onClick={handleTapTempo}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="tap-tempo"
            >
              {t('metronome.tapTempo')}
            </Button>
          </div>
        </motion.div>

        {/* Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#121212] border border-white/5 rounded-2xl p-6"
        >
          <h3 className="font-heading text-lg font-bold text-white mb-4">{t('metronome.settings')}</h3>

          <div className="space-y-4">
            {/* Volume */}
            <div>
              <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                <Volume2 className="w-4 h-4" /> {t('metronome.volume')}
              </label>
              <Slider
                value={[volume * 100]}
                onValueChange={(val) => setVolume(val[0] / 100)}
                min={0}
                max={100}
                step={5}
              />
            </div>

            {/* Subdivision - PRO only */}
            <div>
              <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                {t('metronome.subdivision')}
                {!isPremium && <Lock className="w-3 h-3" />}
              </label>
              {isPremium ? (
                <div className="flex gap-2">
                  {[
                    { value: 'quarter', label: '1/4' },
                    { value: 'eighth', label: '1/8' },
                    { value: 'sixteenth', label: '1/16' }
                  ].map((opt) => (
                    <Button
                      key={opt.value}
                      onClick={() => setSubdivision(opt.value)}
                      size="sm"
                      className={`flex-1 ${
                        subdivision === opt.value
                          ? 'bg-indigo-500 text-white'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                      data-testid={`subdivision-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="bg-zinc-800/50 border border-white/5 rounded-lg p-3 text-center">
                  <Lock className="w-4 h-4 text-zinc-600 mx-auto mb-1" />
                  <p className="text-zinc-500 text-xs">{t('metronome.proOnly')}</p>
                  <Link to="/premium">
                    <Button size="sm" variant="ghost" className="text-amber mt-2">
                      <Crown className="w-3 h-3 mr-1" />
                      {t('dashboard.upgrade')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Time Signature (info only for now) */}
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">{t('metronome.timeSignature')}</label>
              <div className="bg-zinc-800 rounded-lg px-4 py-2 text-center">
                <span className="text-white font-mono text-xl">{beatsPerBar}/4</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MetronomePage;
